import React, { useState } from 'react';
import { TextArea } from '../../../ui/TextArea';
import { FieldInputAdd } from '../../../ui/FieldInputAdd';
import { FieldInput } from '../../../ui/FieldInput';
import { CustomCheckbox } from '../../../ui/CustomCheckbox';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';

import { Recipient, StudentProgramData, BulkProgramInput } from './EmailSenderModal';

const isValidEmail = (email: string): boolean => {
  if (!email || !email.trim()) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

const hasValidEmail = (email: string | null | undefined): boolean => {
  return !!(email && email.trim());
};

interface EmailSenderSettingsPanelProps {
  introText: string;
  setIntroText: (v: string) => void;

  specialists: Recipient[];
  students: Recipient[];

  setSpecialists: any;
  setStudents: any;

  onRemoveRecipient: (id: string | number, list: Recipient[], setList: any) => void;
  onToggleType: (id: string | number, list: Recipient[], setList: any) => void;
  onToggleActive: (id: string | number, list: Recipient[], setList: any) => void;

  onAddSubEmail: (recipientId: string | number, email: string, list: Recipient[], setList: any) => boolean;
  onRemoveSubEmail: (recipientId: string | number, emailIndex: number, list: Recipient[], setList: any) => void;
  onToggleSubEmailType: (recipientId: string | number, emailIndex: number, list: Recipient[], setList: any) => void;

  programsData: StudentProgramData[];

  onDateChange: (studentId: string | number, programName: string, field: keyof StudentProgramData, value: string) => void;

  bulkInputs: Record<string, BulkProgramInput>;
  uniquePrograms: string[];
  onBulkInputChange: (programName: string, field: keyof BulkProgramInput, value: string) => void;
  onApplyBulk: () => void;

  expandedStudentIds: Set<string | number>;
  setExpandedStudentIds: (ids: Set<string | number>) => void;

  previewRecipientId: string | number | null;
  setPreviewRecipientId: (id: string | number | null) => void;
}

export const EmailSenderSettingsPanel: React.FC<EmailSenderSettingsPanelProps> = ({
  introText, setIntroText,
  specialists, students,
  setSpecialists, setStudents,
  onRemoveRecipient, onToggleType, onToggleActive,
  onAddSubEmail, onRemoveSubEmail,
  onToggleSubEmailType,
  programsData, onDateChange,
  bulkInputs, uniquePrograms, onBulkInputChange, onApplyBulk,
  expandedStudentIds, setExpandedStudentIds,
  previewRecipientId, setPreviewRecipientId
}) => {

  const [subEmailInputs, setSubEmailInputs] = useState<Record<string | number, string>>({});
  const [subEmailErrors, setSubEmailErrors] = useState<Record<string | number, string>>({});

  const toggleStudentExpanded = (studentId: string | number) => {
    const newExpandedIds = new Set(expandedStudentIds);
    if (newExpandedIds.has(studentId)) {
      newExpandedIds.delete(studentId);
    } else {
      newExpandedIds.add(studentId);
    }
    setExpandedStudentIds(newExpandedIds);
  };

  const handleSubInputChange = (id: string | number, value: string) => {
    setSubEmailInputs(prev => ({ ...prev, [id]: value }));
    if (subEmailErrors[id]) {
      setSubEmailErrors(prev => ({ ...prev, [id]: '' }));
    }
  };

  const handleAddSubEmailWithValidation = (
    recipientId: string | number,
    email: string,
    list: Recipient[],
    setList: any
  ) => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setSubEmailErrors(prev => ({ ...prev, [recipientId]: 'Введите email' }));
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setSubEmailErrors(prev => ({ ...prev, [recipientId]: 'Некорректный формат email' }));
      return;
    }

    const recipient = list.find((r: Recipient) => r.id === recipientId);
    if (recipient) {
      const isDuplicate = recipient.additionalEmails.some(
        (e: any) => e.address.toLowerCase() === trimmedEmail.toLowerCase()
      );
      if (isDuplicate) {
        setSubEmailErrors(prev => ({ ...prev, [recipientId]: 'Этот email уже добавлен' }));
        return;
      }
      if (recipient.email && recipient.email.toLowerCase() === trimmedEmail.toLowerCase()) {
        setSubEmailErrors(prev => ({ ...prev, [recipientId]: 'Совпадает с основным email' }));
        return;
      }
    }

    onAddSubEmail(recipientId, trimmedEmail, list, setList);
    handleSubInputChange(recipientId, '');
    setSubEmailErrors(prev => ({ ...prev, [recipientId]: '' }));
  };

  const renderRecipientRow = (item: Recipient, list: Recipient[], setList: any) => {
    const isSelected = previewRecipientId === item.id;
    const currentSubInputValue = subEmailInputs[item.id] || '';
    const currentSubEmailError = subEmailErrors[item.id] || '';

    const hasNoEmail = !hasValidEmail(item.email);

    const showCopySection = item.isActive && !hasNoEmail;

    return (
      <div
        key={item.id}
        onClick={() => setPreviewRecipientId(item.id)}
        className={`
            group flex flex-col p-3 border rounded-lg mb-3 transition-all cursor-pointer
            ${isSelected
              ? 'bg-blue-50 border-blue-200'
              : 'bg-white border-gray-200 hover:bg-gray-50'
            }
            ${!item.isActive ? 'opacity-60' : ''}
        `}
      >
        <div className="flex items-center justify-between w-full mb-2">
            <div className="flex flex-col overflow-hidden mr-2">
                <span
                  className={`text-sm font-medium truncate ${
                    isSelected ? 'text-blue-800' : 'text-gray-900'
                  }`}
                  title={item.name}
                >
                    {item.name}
                </span>
                {hasNoEmail ? (
                  <span className="text-xs text-blue-500 font-medium">
                    Email не указан (письмо не будет отправлено)
                  </span>
                ) : (
                  <span className="text-xs text-gray-500 truncate flex items-center gap-1">
                    {item.email}
                  </span>
                )}
            </div>

            <div className="flex items-center gap-2 shrink-0">
                <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!hasNoEmail && item.isActive) {
                        onToggleType(item.id, list, setList);
                      }
                    }}
                    disabled={!item.isActive || hasNoEmail}
                    className={`
                    h-6 px-2 flex items-center justify-center rounded text-[10px] font-bold tracking-wider
                    transition-all duration-200 ease-in-out shadow-sm outline-none
                    ${item.type === 'to'
                        ? 'bg-blue-100 text-blue-700 border border-blue-200'
                        : 'bg-purple-100 text-purple-700 border border-purple-200'
                    }
                    ${(!item.isActive || hasNoEmail) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-opacity-80'}
                    `}
                    title={hasNoEmail ? 'Email не указан' : (item.type === 'to' ? 'CC' : 'BCC')}
                >
                    {item.type === 'to' ? 'CC' : 'BCC'}
                </button>

                <div
                    className="h-6 w-6 flex items-center justify-center cursor-pointer"
                    onClick={(e) => e.stopPropagation()}
                    title={item.isActive ? 'Выключить из отправки' : 'Включить в отправку'}
                >
                    <CustomCheckbox
                        id={`recipient-check-${item.id}`}
                        checked={item.isActive}
                        onChange={() => onToggleActive(item.id, list, setList)}
                        disabled={false}
                    />
                </div>

                <button
                    onClick={(e) => { e.stopPropagation(); onRemoveRecipient(item.id, list, setList); }}
                    className="flex items-center justify-center h-6 w-6 bg-gray-100 text-gray-400 rounded hover:bg-red-500 hover:text-white active:bg-red-700 transition-colors outline-none"
                    title="Исключить из заявки (добавить в exclude)"
                >
                    <CloseIcon style={{ fontSize: 14 }} />
                </button>
            </div>
        </div>

        {showCopySection && (
            <div className="mt-1 pl-1 border-t border-gray-100 pt-2" onClick={(e) => e.stopPropagation()}>
                {item.additionalEmails && item.additionalEmails.length > 0 && (
                    <div className="mb-2 space-y-1">
                        <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider mb-1">Копии этого письма:</p>
                        {item.additionalEmails.map((email, idx) => (
                            <div key={idx} className="flex items-center justify-between bg-white border border-gray-100 rounded px-2 py-1 shadow-sm">
                                <div className="flex items-center gap-2 text-xs text-gray-600 truncate">
                                    <button
                                        type="button"
                                        onClick={() => onToggleSubEmailType(item.id, idx, list, setList)}
                                        className={`
                                            px-1.5 py-0.5 rounded text-[9px] font-bold uppercase border transition
                                            ${email.type === 'cc'
                                                ? 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200'
                                                : 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200'}
                                        `}
                                        title="Переключить между CC и BCC"
                                    >
                                        {email.type}
                                    </button>
                                    <span className="truncate">{email.address}</span>
                                </div>
                                <button
                                    onClick={() => onRemoveSubEmail(item.id, idx, list, setList)}
                                    className="flex items-center justify-center h-5 w-5 bg-gray-100 text-gray-400 rounded hover:bg-red-500 hover:text-white active:bg-red-700 transition-colors outline-none ml-2"
                                    title="Удалить копию"
                                >
                                    <CloseIcon style={{ fontSize: 12 }} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="relative">
                    <FieldInputAdd
                        value={currentSubInputValue}
                        onChange={(val) => handleSubInputChange(item.id, val)}
                        onAdd={() => {
                            handleAddSubEmailWithValidation(item.id, currentSubInputValue, list, setList);
                        }}
                        placeholder="Добавить email для копии..."
                        icon={<ForwardToInboxIcon fontSize="small" className="text-gray-400" />}
                        className="text-xs"
                        error={currentSubEmailError}
                    />
                </div>
            </div>
        )}
      </div>
    );
  };

  return (
    <div className="col-span-5 bg-gray-50 border-r border-gray-200 h-full overflow-y-auto p-6 space-y-5 custom-scrollbar">

      {/* Вступительный текст */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-2 text-gray-700 font-semibold text-sm">
           <EditIcon fontSize="small" /> Вступительный текст
        </div>
        <TextArea
          label=""
          value={introText}
          onChange={setIntroText}
          minRows={4}
          maxAutoHeight={200}
          className="text-sm"
        />
        <p className="text-xs text-gray-400 mt-1">Этот текст будет отображаться в начале письма</p>
      </div>

      {/* Ответственные лица */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-3 text-gray-700 font-semibold text-sm">
          <PersonIcon fontSize="small" /> Ответственные лица
        </div>
        <div className="space-y-2 mb-3">
          {specialists.map(spec => renderRecipientRow(spec, specialists, setSpecialists))}
        </div>
      </div>

      {/* Студенты */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-3 text-gray-700 font-semibold text-sm">
          <SchoolIcon fontSize="small" /> Студенты
        </div>
        <div className="space-y-2 mb-3 max-h-[500px] overflow-y-auto custom-scrollbar pr-1">
           {students.map(stud => renderRecipientRow(stud, students, setStudents))}
        </div>
      </div>

      {/* Редактирование данных */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-4 text-gray-700 font-semibold text-sm">
          <EditIcon fontSize="small" /> Редактирование данных
        </div>

        {uniquePrograms.length > 0 && (
          <div className="mb-6">
              <div className="border border-blue-200 rounded-md overflow-hidden shadow-sm bg-blue-50/30">
                  <div className="bg-blue-100 px-4 py-2 border-b border-blue-200">
                    <h3 className="text-xs font-bold text-blue-800 uppercase tracking-wide">
                        Общие настройки по программам
                    </h3>
                  </div>

                  <div className="flex bg-gray-50 border-b border-gray-200">
                    <div className="flex-1 px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider border-r border-gray-100">Программа</div>
                    <div className="w-28 px-2 py-2 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider border-r border-gray-100">Начало</div>
                    <div className="w-28 px-2 py-2 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider border-r border-gray-100">Окончание</div>
                    <div className="w-28 px-2 py-2 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider">Экзамен</div>
                  </div>

                  {uniquePrograms.map(progName => (
                      <div key={progName} className="flex border-b border-gray-100 last:border-none bg-white items-center">
                          <div className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 border-r border-gray-100 leading-tight">
                              {progName}
                          </div>
                          <div className="w-28 px-1 py-1 border-r border-gray-100">
                              <FieldInput
                                  label=""
                                  value={bulkInputs[progName]?.startDate || ''}
                                  onChange={(v) => onBulkInputChange(progName, 'startDate', v)}
                                  mask="00.00.0000"
                                  placeholder="dd.mm.yyyy"
                                  className="-mt-1.5 w-full text-center text-xs"
                              />
                          </div>
                          <div className="w-28 px-1 py-1 border-r border-gray-100">
                              <FieldInput
                                  label=""
                                  value={bulkInputs[progName]?.endDate || ''}
                                  onChange={(v) => onBulkInputChange(progName, 'endDate', v)}
                                  mask="00.00.0000"
                                  placeholder="dd.mm.yyyy"
                                  className="-mt-1.5 w-full text-center text-xs"
                              />
                          </div>
                          <div className="w-28 px-1 py-1">
                              <FieldInput
                                  label=""
                                  value={bulkInputs[progName]?.examDate || ''}
                                  onChange={(v) => onBulkInputChange(progName, 'examDate', v)}
                                  mask="00.00.0000"
                                  placeholder="dd.mm.yyyy"
                                  className="-mt-1.5 w-full text-center text-xs"
                              />
                          </div>
                      </div>
                  ))}
              </div>

              <div className="mt-2 flex justify-end">
                  <button
                      onClick={onApplyBulk}
                      className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition shadow-sm outline-none"
                  >
                      Применить
                  </button>
              </div>
          </div>
        )}

        <div className="space-y-2">
           {students.map(student => {
             if (student.role !== 'student') return null;

             const isExpanded = expandedStudentIds.has(student.id);
             const studentPrograms = programsData.filter(p => p.studentId === student.id);

             return (
               <div key={student.id} className="border rounded-md overflow-hidden shadow-sm">
                  <button
                    onClick={() => toggleStudentExpanded(student.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 text-xs font-bold uppercase tracking-wide transition outline-none ${
                        isExpanded
                        ? 'bg-blue-100 text-blue-800 border-b border-blue-200'
                        : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <span>{student.name}</span>
                    {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  </button>

                  {isExpanded && (
                    <div className="p-0 bg-white">
                       {studentPrograms.length === 0 ? (
                         <p className="text-xs text-gray-400 p-4">Нет программ</p>
                       ) : (
                         <div className="overflow-x-auto">
                           <div className="flex bg-gray-50 border-b border-gray-200">
                             <div className="flex-1 px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase tracking-wider border-r border-gray-100">
                                 Программа
                             </div>
                             <div className="w-28 px-2 py-2 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider border-r border-gray-100">
                                 Дата начала
                             </div>
                             <div className="w-28 px-2 py-2 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider border-r border-gray-100">
                                 Дата окончания
                             </div>
                             <div className="w-28 px-2 py-2 text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider">
                                 Дата экзамена
                             </div>
                           </div>

                           {studentPrograms.map((prog, idx) => (
                             <div key={idx} className="flex border-b border-gray-100 hover:bg-gray-50 transition-colors items-center">
                               <div className="flex-1 px-4 py-3 text-xs text-gray-900 font-medium border-r border-gray-100">
                                 {prog.programName}
                               </div>

                               <div className="w-28 px-2 py-2 border-r border-gray-100 flex items-center justify-center">
                                     <FieldInput
                                        label=""
                                        value={prog.startDate}
                                        onChange={(val) => onDateChange(student.id, prog.programName, 'startDate', val)}
                                        mask="00.00.0000"
                                        placeholder="dd.mm.yyyy"
                                        className="-mt-1.5 w-full text-center text-xs"
                                     />
                               </div>

                               <div className="w-28 px-2 py-2 border-r border-gray-100 flex items-center justify-center">
                                     <FieldInput
                                        label=""
                                        value={prog.endDate}
                                        onChange={(val) => onDateChange(student.id, prog.programName, 'endDate', val)}
                                        mask="00.00.0000"
                                        placeholder="dd.mm.yyyy"
                                        className="-mt-1.5 w-full text-center text-xs"
                                     />
                               </div>

                               <div className="w-28 px-2 py-2 flex items-center justify-center">
                                     <FieldInput
                                        label=""
                                        value={prog.examDate}
                                        onChange={(val) => onDateChange(student.id, prog.programName, 'examDate', val)}
                                        mask="00.00.0000"
                                        placeholder="dd.mm.yyyy"
                                        className="-mt-1.5 w-full text-center text-xs"
                                     />
                               </div>
                             </div>
                           ))}
                         </div>
                       )}
                    </div>
                  )}
               </div>
             );
           })}
        </div>
      </div>
    </div>
  );
};